import React, { useState } from 'react';
import { useCondominium } from '../context/CondominiumContext';
import CondominiumForm from './CondominiumForm';
import CondominiumDetailModal from './CondominiumDetailModal';
import { translateAmenity } from '../utils/translations';
import { Condominium } from '../types';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MapPin,
  Building,
  Calendar,
  Tag,
  Grid3x3,
  List,
  Eye
} from 'lucide-react';

const CondominiumList: React.FC = () => {
  const { condominiums, addCondominium, updateCondominium, deleteCondominium } = useCondominium();
  const [showForm, setShowForm] = useState(false);
  const [editingCondominium, setEditingCondominium] = useState<Condominium | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [viewingCondominium, setViewingCondominium] = useState<Condominium | undefined>();

  const filteredCondominiums = condominiums.filter(condominium => {
    const matchesSearch = condominium.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         condominium.address.neighborhood.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         condominium.address.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  }).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const handleSave = (condominium: Condominium) => {
    if (editingCondominium) {
      updateCondominium(condominium);
    } else {
      addCondominium(condominium);
    }
    setShowForm(false);
    setEditingCondominium(undefined);
  };

  const handleEdit = (condominium: Condominium) => {
    setEditingCondominium(condominium);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este condomínio?')) {
      deleteCondominium(id);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Gerenciar Condomínios</h1>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
              {filteredCondominiums.length} de {condominiums.length} condomínios cadastrados no sistema
            </p>
          </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3x3 className="w-3 sm:w-4 h-3 sm:h-4 mr-1" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-3 sm:w-4 h-3 sm:h-4 mr-1" />
              <span className="hidden sm:inline">Lista</span>
            </button>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-semibold hidden sm:inline">Novo Condomínio</span>
            <span className="font-semibold sm:hidden">Novo</span>
          </button>
        </div>
      </div>
    </div>

      {/* Search Bar */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por nome, bairro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Condominiums Display */}
      {filteredCondominiums.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCondominiums.map((condominium) => (
              <div key={condominium.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                  {/* Imagem de capa */}
                  {condominium.images && condominium.images.length > 0 && (
                    <div className="mb-4">
                      <img
                        src={condominium.images[0].url || condominium.images[0].preview}
                        alt={condominium.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">
                      {condominium.name}
                    </h3>
                  </div>
                  
                  <div className="flex items-center text-gray-600 text-sm mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>
                      {condominium.address.neighborhood}, {condominium.address.city} - {condominium.address.state}
                    </span>
                  </div>

                  <div className="flex items-center text-gray-500 text-xs mb-4">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>Criado em {new Date(condominium.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>

                  {/* Features */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <Tag className="w-4 h-4 mr-1 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {condominium.features.length} características
                        {condominium.images && condominium.images.length > 0 && (
                          <span className="ml-2">• {condominium.images.length} imagens</span>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {condominium.features.slice(0, 3).map((feature, index) => (
                        <span
                          key={index}
                          className="bg-orange-50 border border-orange-200 text-orange-800 px-2 py-1 rounded text-xs font-medium"
                        >
                          {translateAmenity(feature)}
                        </span>
                      ))}
                      {condominium.features.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          +{condominium.features.length - 3} mais
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      ID: {condominium.id}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setViewingCondominium(condominium)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(condominium)}
                        className="text-orange-600 hover:text-orange-800 p-1 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(condominium.id)}
                        className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[300px]">
                      Condomínio
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Localização
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                      Data Criação
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                      Características
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCondominiums.map((condominium) => (
                    <tr key={condominium.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          {condominium.images && condominium.images.length > 0 && (
                            <img
                              src={condominium.images[0].url || condominium.images[0].preview}
                              alt={condominium.name}
                              className="w-12 h-12 object-cover rounded-lg mr-3 flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {condominium.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              ID: {condominium.id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-gray-900">
                          {condominium.address.neighborhood}
                        </div>
                        <div className="text-xs text-gray-500">
                          {condominium.address.city} - {condominium.address.state}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900">
                        {new Date(condominium.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-gray-900">
                          {condominium.features.length} características
                        </div>
                        <div className="text-xs text-gray-500">
                          {condominium.images && condominium.images.length > 0 && (
                            <span>{condominium.images.length} imagens</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => setViewingCondominium(condominium)}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                            title="Visualizar"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(condominium)}
                            className="text-orange-600 hover:text-orange-800 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(condominium.id)}
                            className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Nenhum condomínio encontrado
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm 
              ? 'Tente ajustar os filtros para encontrar mais condomínios'
              : 'Comece adicionando seu primeiro condomínio'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Adicionar Condomínio
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <CondominiumForm
          condominium={editingCondominium}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingCondominium(undefined);
          }}
        />
      )}

      {/* Detail Modal */}
      {viewingCondominium && (
        <CondominiumDetailModal
          condominium={viewingCondominium}
          onClose={() => setViewingCondominium(undefined)}
        />
      )}
    </div>
  );
};

export default CondominiumList;